<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TeamPresenceAPI from 'dashboard/api/teletel/teamPresence';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

// «حضور الفريق» (owner order 2026-09-26): the real dashboard connection next
// to the status each agent declared, last activity, open load, and a summon
// that reaches only agents who are really connected.
const REFRESH_EVERY = 30000;
const IDLE_CHOICES = [15, 30, 60];
const ESCALATE_CHOICES = [2, 5, 10, 15, 30];

const { t, te, locale } = useI18n();

const agents = ref([]);
const loading = ref(true);
const now = ref(Date.now());
const teamFilter = ref('');
const onlyAlerts = ref(false);
const idleMinutes = ref(30);
const selected = ref([]);
const note = ref('');
const escalateAfter = ref(5);
const escalateTo = ref('');
const sending = ref(false);
const windowSeconds = ref(90);
const windowChoices = ref([]);
const canConfigure = ref(false);
let timer = null;

const minutesSince = iso =>
  iso ? Math.floor((now.value - new Date(iso).getTime()) / 60000) : null;

const relative = iso => {
  const minutes = minutesSince(iso);
  if (minutes === null) return t('TELETEL_TEAM_PRESENCE.NEVER');
  const format = new Intl.RelativeTimeFormat(locale.value, { numeric: 'auto' });
  if (minutes < 60) return format.format(-minutes, 'minute');
  if (minutes < 1440) return format.format(-Math.floor(minutes / 60), 'hour');
  return format.format(-Math.floor(minutes / 1440), 'day');
};

// What the team sees: with auto-offline on, a closed dashboard shows offline.
const shownStatus = agent => {
  if (agent.connected || !agent.auto_offline) return agent.declared;
  return 'offline';
};

const alertFor = agent => {
  if (agent.connected && agent.declared === 'offline') return 'HIDDEN';
  if (!agent.connected && !agent.auto_offline && agent.declared !== 'offline')
    return 'GHOST';
  if (agent.connected && agent.declared === 'online') {
    const idle = minutesSince(agent.last_reply_at);
    if (
      agent.open_conversations > 0 &&
      (idle === null || idle >= idleMinutes.value)
    )
      return 'IDLE';
  }
  return null;
};

const teams = computed(() =>
  [...new Set(agents.value.flatMap(agent => agent.teams))].sort()
);

// Sorting (owner order 2026-09-27): by default the most recently connected
// agent is on top; every column header sorts ascending/descending. Empty
// values (never seen, no reply today) always stay at the bottom.
const DECLARED_RANK = { online: 3, busy: 2, offline: 1 };
const ALERT_RANK = { GHOST: 3, HIDDEN: 2, IDLE: 1 };
const time = iso => (iso ? new Date(iso).getTime() : null);
const SORT_VALUES = {
  AGENT: agent => agent.name,
  REAL: agent => (agent.connected ? 1 : 0),
  DECLARED: agent => DECLARED_RANK[agent.declared] || 0,
  // A connected agent was seen now, whatever the last stored heartbeat says.
  LAST_SEEN: agent => (agent.connected ? now.value : time(agent.last_seen_at)),
  LAST_REPLY: agent => time(agent.last_reply_at),
  OPEN: agent => agent.open_conversations,
  ALERT: agent => (agent.alert ? ALERT_RANK[agent.alert] : null),
};
const SORT_COLUMNS = Object.keys(SORT_VALUES);
const sortKey = ref('LAST_SEEN');
const sortDir = ref('desc');

const setSort = key => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc';
  } else {
    sortKey.value = key;
    sortDir.value = key === 'AGENT' ? 'asc' : 'desc';
  }
};

const compareRows = (a, b) => {
  const value = SORT_VALUES[sortKey.value];
  const va = value(a);
  const vb = value(b);
  if (va === null && vb === null) return a.name.localeCompare(b.name);
  if (va === null) return 1;
  if (vb === null) return -1;
  const diff = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
  const directed = sortDir.value === 'asc' ? diff : -diff;
  return (
    directed ||
    (time(b.last_seen_at) || 0) - (time(a.last_seen_at) || 0) ||
    a.name.localeCompare(b.name)
  );
};

const sortArrow = key => {
  if (sortKey.value !== key) return '';
  return sortDir.value === 'asc' ? '▲' : '▼';
};

const rows = computed(() =>
  agents.value
    .filter(
      agent => !teamFilter.value || agent.teams.includes(teamFilter.value)
    )
    .map(agent => ({
      ...agent,
      shown: shownStatus(agent),
      alert: alertFor(agent),
    }))
    .filter(agent => !onlyAlerts.value || agent.alert)
    .sort(compareRows)
);

const counts = computed(() => ({
  connected: agents.value.filter(agent => agent.connected).length,
  total: agents.value.length,
  alerts: agents.value.filter(agent => alertFor(agent)).length,
}));

const connectedSelected = computed(() =>
  agents.value.filter(
    agent => selected.value.includes(agent.user_id) && agent.connected
  )
);

const STATUS_CLASS = {
  online: 'bg-n-teal-9',
  busy: 'bg-n-amber-9',
  offline: 'bg-n-slate-9',
};

const load = async () => {
  try {
    const { data } = await TeamPresenceAPI.list();
    agents.value = data.agents;
    windowSeconds.value = data.presence_window_seconds;
    windowChoices.value = data.presence_window_choices;
    canConfigure.value = data.can_configure;
    now.value = Date.now();
  } catch {
    useAlert(t('TELETEL_TEAM_PRESENCE.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const toggle = userId => {
  selected.value = selected.value.includes(userId)
    ? selected.value.filter(id => id !== userId)
    : [...selected.value, userId];
};

// Only administrators may summon administrators (the server enforces it).
const canSummon = agent => canConfigure.value || agent.role !== 'administrator';

const selectConnectedInView = () => {
  selected.value = rows.value
    .filter(agent => agent.connected && canSummon(agent))
    .map(agent => agent.user_id);
};

const sendSummon = async () => {
  sending.value = true;
  try {
    const { data } = await TeamPresenceAPI.summon({
      userIds: selected.value,
      note: note.value,
      escalateToId: escalateTo.value || null,
      escalateAfterMinutes: escalateAfter.value,
    });
    useAlert(
      t('TELETEL_TEAM_PRESENCE.SUMMON.SENT', {
        delivered: data.delivered_to.length,
        skipped: data.skipped.length,
      })
    );
    selected.value = [];
    note.value = '';
  } catch (error) {
    const code = error?.response?.data?.error;
    const key = `TELETEL_TEAM_PRESENCE.SUMMON.ERRORS.${code}`;
    useAlert(t(te(key) ? key : 'TELETEL_TEAM_PRESENCE.SUMMON.ERRORS.GENERIC'));
  } finally {
    sending.value = false;
  }
};

// Only administrators change how long after the last heartbeat an agent
// still counts as connected.
const saveWindow = async seconds => {
  try {
    await TeamPresenceAPI.updateWindow(seconds);
    windowSeconds.value = seconds;
    useAlert(t('TELETEL_TEAM_PRESENCE.WINDOW.SAVED'));
    load();
  } catch {
    useAlert(t('TELETEL_TEAM_PRESENCE.WINDOW.ERROR'));
  }
};

// Refresh only while the page is visible; catch up when it comes back.
const onVisibility = () => {
  if (!document.hidden) load();
};

onMounted(() => {
  load();
  timer = setInterval(() => {
    if (!document.hidden) load();
  }, REFRESH_EVERY);
  document.addEventListener('visibilitychange', onVisibility);
});
onBeforeUnmount(() => {
  clearInterval(timer);
  document.removeEventListener('visibilitychange', onVisibility);
});
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader
      :title="t('TELETEL_TEAM_PRESENCE.HEADER')"
      :description="t('TELETEL_TEAM_PRESENCE.DESCRIPTION')"
    >
      <template #actions>
        <NextButton
          slate
          faded
          sm
          icon="i-lucide-refresh-ccw"
          :label="t('TELETEL_TEAM_PRESENCE.REFRESH')"
          @click="load"
        />
      </template>
    </PageHeader>

    <div
      class="flex flex-wrap items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 text-sm"
    >
      <span class="font-medium text-n-slate-12">
        {{ t('TELETEL_TEAM_PRESENCE.WINDOW.LABEL') }}
      </span>
      <select
        v-if="canConfigure"
        :value="windowSeconds"
        class="!mb-0 !w-auto rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1"
        @change="saveWindow(Number($event.target.value))"
      >
        <option v-for="s in windowChoices" :key="s" :value="s">
          {{ t('TELETEL_TEAM_PRESENCE.WINDOW.SECONDS', { count: s }) }}
        </option>
      </select>
      <span v-else class="text-n-slate-12">
        {{
          t('TELETEL_TEAM_PRESENCE.WINDOW.SECONDS', { count: windowSeconds })
        }}
      </span>
      <span class="text-n-slate-11">
        {{ t('TELETEL_TEAM_PRESENCE.WINDOW.HELP') }}
      </span>
    </div>

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="text-sm text-n-slate-11">
          {{ t('TELETEL_TEAM_PRESENCE.STATS.CONNECTED') }}
        </p>
        <p class="text-2xl font-semibold text-n-teal-11">
          {{ counts.connected }} / {{ counts.total }}
        </p>
      </div>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="text-sm text-n-slate-11">
          {{ t('TELETEL_TEAM_PRESENCE.STATS.ALERTS') }}
        </p>
        <p class="text-2xl font-semibold text-n-ruby-11">
          {{ counts.alerts }}
        </p>
      </div>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <p class="text-sm text-n-slate-11">
          {{ t('TELETEL_TEAM_PRESENCE.STATS.SELECTED') }}
        </p>
        <p class="text-2xl font-semibold text-n-slate-12">
          {{ connectedSelected.length }}
        </p>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-3 text-sm">
      <select
        v-model="teamFilter"
        class="!mb-0 !w-auto rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5"
      >
        <option value="">
          {{ t('TELETEL_TEAM_PRESENCE.FILTERS.ALL_TEAMS') }}
        </option>
        <option v-for="team in teams" :key="team" :value="team">
          {{ team }}
        </option>
      </select>
      <label class="flex items-center gap-2">
        <input v-model="onlyAlerts" type="checkbox" />
        {{ t('TELETEL_TEAM_PRESENCE.FILTERS.ONLY_ALERTS') }}
      </label>
      <label class="flex items-center gap-2">
        {{ t('TELETEL_TEAM_PRESENCE.FILTERS.IDLE_AFTER') }}
        <select
          v-model.number="idleMinutes"
          class="!mb-0 !w-auto rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1"
        >
          <option v-for="m in IDLE_CHOICES" :key="m" :value="m">
            {{ t('TELETEL_TEAM_PRESENCE.MINUTES', { count: m }) }}
          </option>
        </select>
      </label>
      <NextButton
        slate
        link
        sm
        :label="t('TELETEL_TEAM_PRESENCE.SELECT_CONNECTED')"
        @click="selectConnectedInView"
      />
    </div>

    <p v-if="loading" class="text-sm text-n-slate-11">
      {{ t('TELETEL_TEAM_PRESENCE.LOADING') }}
    </p>
    <div v-else class="overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full min-w-[760px] text-sm">
        <thead class="bg-n-solid-2 text-n-slate-11">
          <tr>
            <th class="w-8 p-3" />
            <th
              v-for="column in SORT_COLUMNS"
              :key="column"
              class="p-3 text-start"
              :aria-sort="
                sortKey === column
                  ? sortDir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
              "
            >
              <button
                type="button"
                class="inline-flex items-center gap-1 font-medium hover:text-n-slate-12"
                :class="sortKey === column ? 'text-n-slate-12' : ''"
                :title="t('TELETEL_TEAM_PRESENCE.SORT_HINT')"
                @click="setSort(column)"
              >
                {{ t(`TELETEL_TEAM_PRESENCE.COLUMNS.${column}`) }}
                <span class="text-xs">{{ sortArrow(column) }}</span>
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="agent in rows"
            :key="agent.user_id"
            class="border-t border-n-weak"
            :class="agent.alert ? 'bg-n-ruby-2' : ''"
          >
            <td class="p-3">
              <input
                type="checkbox"
                :disabled="!canSummon(agent)"
                :title="
                  canSummon(agent)
                    ? ''
                    : t('TELETEL_TEAM_PRESENCE.ADMIN_ONLY_SUMMON')
                "
                :checked="selected.includes(agent.user_id)"
                @change="toggle(agent.user_id)"
              />
            </td>
            <td class="p-3">
              <div class="flex items-center gap-2">
                <Avatar
                  :name="agent.name"
                  :src="agent.avatar_url"
                  :size="28"
                  rounded-full
                />
                <div class="min-w-0">
                  <p class="truncate font-medium text-n-slate-12">
                    {{ agent.name }}
                  </p>
                  <p class="truncate text-xs text-n-slate-11">
                    {{ agent.teams.join('، ') }}
                  </p>
                </div>
              </div>
            </td>
            <td class="p-3">
              <span
                class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs"
                :class="
                  agent.connected
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                <span
                  class="size-2 rounded-full"
                  :class="agent.connected ? 'bg-n-teal-9' : 'bg-n-slate-8'"
                />
                {{
                  agent.connected
                    ? t('TELETEL_TEAM_PRESENCE.REAL.CONNECTED')
                    : t('TELETEL_TEAM_PRESENCE.REAL.DISCONNECTED')
                }}
              </span>
            </td>
            <td class="p-3">
              <span class="inline-flex items-center gap-1.5">
                <span
                  class="size-2 rounded-sm"
                  :class="STATUS_CLASS[agent.declared]"
                />
                {{
                  t(
                    `TELETEL_TEAM_PRESENCE.STATUS.${agent.declared.toUpperCase()}`
                  )
                }}
              </span>
              <p v-if="!agent.auto_offline" class="text-xs text-n-amber-11">
                {{ t('TELETEL_TEAM_PRESENCE.FIXED_STATUS') }}
              </p>
            </td>
            <td class="p-3 text-n-slate-11">
              {{
                agent.connected
                  ? t('TELETEL_TEAM_PRESENCE.NOW')
                  : relative(agent.last_seen_at)
              }}
            </td>
            <td class="p-3 text-n-slate-11">
              {{
                agent.last_reply_at
                  ? relative(agent.last_reply_at)
                  : t('TELETEL_TEAM_PRESENCE.NO_REPLY_TODAY')
              }}
            </td>
            <td class="p-3">{{ agent.open_conversations }}</td>
            <td class="p-3">
              <span
                v-if="agent.alert"
                class="text-xs font-medium text-n-ruby-11"
              >
                {{ t(`TELETEL_TEAM_PRESENCE.ALERTS.${agent.alert}`) }}
              </span>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="8" class="p-6 text-center text-n-slate-11">
              {{ t('TELETEL_TEAM_PRESENCE.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <section
      class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('TELETEL_TEAM_PRESENCE.SUMMON.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('TELETEL_TEAM_PRESENCE.SUMMON.HELP') }}
      </p>
      <textarea
        v-model="note"
        rows="2"
        maxlength="300"
        class="!mb-0 rounded-lg border border-n-weak bg-n-solid-2 p-2 text-sm"
        :placeholder="t('TELETEL_TEAM_PRESENCE.SUMMON.NOTE_PLACEHOLDER')"
      />
      <div class="flex flex-wrap items-center gap-3 text-sm">
        <label class="flex items-center gap-2">
          {{ t('TELETEL_TEAM_PRESENCE.SUMMON.ESCALATE_AFTER') }}
          <select
            v-model.number="escalateAfter"
            class="!mb-0 !w-auto rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1"
          >
            <option v-for="m in ESCALATE_CHOICES" :key="m" :value="m">
              {{ t('TELETEL_TEAM_PRESENCE.MINUTES', { count: m }) }}
            </option>
          </select>
        </label>
        <label class="flex items-center gap-2">
          {{ t('TELETEL_TEAM_PRESENCE.SUMMON.ESCALATE_TO') }}
          <select
            v-model="escalateTo"
            class="!mb-0 !w-auto rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1"
          >
            <option value="">
              {{ t('TELETEL_TEAM_PRESENCE.SUMMON.ESCALATE_ME_ONLY') }}
            </option>
            <option
              v-for="agent in agents"
              :key="agent.user_id"
              :value="agent.user_id"
            >
              {{ agent.name }}
            </option>
          </select>
        </label>
        <NextButton
          sm
          ruby
          icon="i-lucide-bell-ring"
          :is-loading="sending"
          :disabled="!connectedSelected.length || sending"
          :label="
            t('TELETEL_TEAM_PRESENCE.SUMMON.SEND', {
              count: connectedSelected.length,
            })
          "
          @click="sendSummon"
        />
      </div>
    </section>
  </div>
</template>
